import React, { useState } from 'react';
import { ArrowRight, Users, Globe, Target, Lightbulb, Handshake } from 'lucide-react';
import { Button } from '../components/ui/button';
import VentureCard from '../components/VentureCard';
import PartnershipForm from '../components/PartnershipForm';
import { ventures } from '../mock';

const Home = () => {
  const [isPartnershipFormOpen, setIsPartnershipFormOpen] = useState(false);

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20 bg-[#ffed00]">
        {/* Decorative organic shapes */}
        <div className="absolute top-20 right-20 w-64 h-64 bg-[#faba00] organic-blob opacity-60"></div>
        <div className="absolute bottom-20 left-10 w-96 h-96 bg-black organic-blob-2 opacity-5"></div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 py-20 text-center">
          <div className="mb-8 inline-block">
            <img
              src="https://customer-assets.emergentagent.com/job_13664fd4-6d2c-4c22-baf5-9a2dd54779d6/artifacts/0ucf8pui_Digital_Inclusion_Lab_2026_Logo-removebg-preview.png"
              alt="Digital Inclusion Lab Logo"
              className="h-32 w-auto mx-auto animate-float"
            />
          </div>
          
          <h1 className="text-5xl md:text-7xl font-bold mb-6 text-black leading-tight">
            Bridging the Digital Divide with Empathy
          </h1>
          
          <p className="text-xl md:text-2xl text-black/90 mb-4 max-w-4xl mx-auto leading-relaxed font-semibold">
            Where Tech meets Social Impact
          </p>

          <p className="text-lg md:text-xl text-black/80 mb-12 max-w-4xl mx-auto leading-relaxed">
            The global digital revolution is accelerating, yet millions are left behind. 
            Digital Inclusion Lab builds and partners with ventures to create a radically 
            inclusive and connected world.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="#ventures">
              <Button
                size="lg"
                className="bg-[#ff8385] hover:bg-[#ff6b6d] border-4 border-black text-black font-bold px-8 py-6 text-lg rounded-full shadow-2xl hover:shadow-3xl transition-all duration-300 hover:scale-105"
              >
                Explore Our Ventures
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </a>
            <a href="#about">
              <Button
                size="lg"
                variant="outline"
                className="border-4 border-black text-black hover:bg-black hover:text-white font-bold px-8 py-6 text-lg rounded-full transition-all duration-300 hover:scale-105"
              >
                Learn More About Us
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-gray-900">
              From Concept to <span className="text-[#ffed00] brush-underline">Impact</span>: How We Work
            </h2>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
              Have an idea that can make the digital world more inclusive? Here is how we partner 
              with innovators and builders to bring impactful solutions to life.
            </p>
          </div>

          {/* The 6-Step Journey */}
          <div className="max-w-5xl mx-auto">
            <h3 className="text-2xl font-bold text-center mb-12 text-gray-900">
              The <span className="text-[#ff8385]">6-Step</span> Journey
            </h3>

            <div className="space-y-8">
              {/* Step 1 */}
              <div className="flex items-start gap-6 group">
                <div className="w-16 h-16 bg-[#ffed00] rounded-full flex items-center justify-center flex-shrink-0 text-black font-bold text-2xl shadow-lg group-hover:scale-110 transition-transform duration-300">
                  1
                </div>
                <div className="flex-1 bg-gradient-to-br from-gray-50 to-white rounded-2xl p-6 border-2 border-gray-100 hover:border-[#4eb5e6]/30 transition-all duration-300 hover:shadow-lg">
                  <h4 className="text-xl font-bold text-gray-900 mb-3">Submit Your Idea</h4>
                  <p className="text-gray-600 leading-relaxed">
                    Share your vision with us through our simple submission form. Rest assured, our transparent 
                    Terms & Conditions strictly ensure that your idea remains entirely your Intellectual Property (IP).
                  </p>
                </div>
              </div>

              {/* Step 2 */}
              <div className="flex items-start gap-6 group">
                <div className="w-16 h-16 bg-[#ff8385] rounded-full flex items-center justify-center flex-shrink-0 text-black font-bold text-2xl shadow-lg group-hover:scale-110 transition-transform duration-300">
                  2
                </div>
                <div className="flex-1 bg-gradient-to-br from-gray-50 to-white rounded-2xl p-6 border-2 border-gray-100 hover:border-[#ff8385]/30 transition-all duration-300 hover:shadow-lg">
                  <h4 className="text-xl font-bold text-gray-900 mb-3">Review & Connect</h4>
                  <p className="text-gray-600 leading-relaxed">
                    Our core team will deeply evaluate your proposal to see how it aligns with our inclusion goals. 
                    If there is a fit, we will get on a call with you to discuss the possibilities.
                  </p>
                </div>
              </div>

              {/* Step 3 */}
              <div className="flex items-start gap-6 group">
                <div className="w-16 h-16 bg-[#ffed00] rounded-full flex items-center justify-center flex-shrink-0 text-black font-bold text-2xl shadow-lg group-hover:scale-110 transition-transform duration-300">
                  3
                </div>
                <div className="flex-1 bg-gradient-to-br from-gray-50 to-white rounded-2xl p-6 border-2 border-gray-100 hover:border-[#4eb5e6]/30 transition-all duration-300 hover:shadow-lg">
                  <h4 className="text-xl font-bold text-gray-900 mb-3">Collaborative Building</h4>
                  <p className="text-gray-600 leading-relaxed">
                    We roll up our sleeves and build together. DIL provides the technical, design, and strategic 
                    support to take your venture from a raw concept to a fully functioning Minimum Viable Product (MVP).
                  </p>
                </div>
              </div>

              {/* Step 4 */}
              <div className="flex items-start gap-6 group">
                <div className="w-16 h-16 bg-[#ff8385] rounded-full flex items-center justify-center flex-shrink-0 text-black font-bold text-2xl shadow-lg group-hover:scale-110 transition-transform duration-300">
                  4
                </div>
                <div className="flex-1 bg-gradient-to-br from-gray-50 to-white rounded-2xl p-6 border-2 border-gray-100 hover:border-[#ff8385]/30 transition-all duration-300 hover:shadow-lg">
                  <h4 className="text-xl font-bold text-gray-900 mb-3">Growth Strategy & Execution</h4>
                  <p className="text-gray-600 leading-relaxed">
                    An impactful tool is only useful if it reaches the people who need it. We work with you to map 
                    out a hyperlocal growth strategy and actively help you start implementing it.
                  </p>
                </div>
              </div>

              {/* Step 5 */}
              <div className="flex items-start gap-6 group">
                <div className="w-16 h-16 bg-[#ffed00] rounded-full flex items-center justify-center flex-shrink-0 text-black font-bold text-2xl shadow-lg group-hover:scale-110 transition-transform duration-300">
                  5
                </div>
                <div className="flex-1 bg-gradient-to-br from-gray-50 to-white rounded-2xl p-6 border-2 border-gray-100 hover:border-[#4eb5e6]/30 transition-all duration-300 hover:shadow-lg">
                  <h4 className="text-xl font-bold text-gray-900 mb-3">Investor Pitching & Scaling</h4>
                  <p className="text-gray-600 leading-relaxed">
                    When the venture is ready to scale, we help you refine your narrative, build your pitch decks, 
                    and present to the right social impact investors to unlock sustainable growth.
                  </p>
                </div>
              </div>

              {/* Step 6 */}
              <div className="flex items-start gap-6 group">
                <div className="w-16 h-16 bg-[#ff8385] rounded-full flex items-center justify-center flex-shrink-0 text-black font-bold text-2xl shadow-lg group-hover:scale-110 transition-transform duration-300">
                  6
                </div>
                <div className="flex-1 bg-gradient-to-br from-gray-50 to-white rounded-2xl p-6 border-2 border-gray-100 hover:border-[#ff8385]/30 transition-all duration-300 hover:shadow-lg">
                  <h4 className="text-xl font-bold text-gray-900 mb-3">Create Impact Together</h4>
                  <p className="text-gray-600 leading-relaxed">
                    We cross the finish line as partners. Together, we deploy the technology to change lives and 
                    build a more connected, equitable society.
                  </p>
                </div>
              </div>
            </div>

            {/* CTA Button */}
            <div className="text-center mt-12">
              <Button
                size="lg"
                onClick={() => setIsPartnershipFormOpen(true)}
                className="bg-[#ff8385] hover:bg-[#ff6b6d] border-4 border-black text-black font-bold px-10 py-6 text-lg rounded-full shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105"
              >
                <Lightbulb className="mr-2 h-5 w-5" />
                Submit Your Idea Now
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Ventures Section */}
      <section id="ventures" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-gray-900">
              Initiatives Driving <span className="text-[#ffed00]">Inclusion</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Each venture is designed with empathy to bridge the digital divide for vulnerable communities.
            </p>
          </div>

          <div className="space-y-12">
            {ventures.map((venture, index) => (
              <VentureCard key={venture.id} venture={venture} index={index} />
            ))}
          </div>
        </div>
      </section>

      {/* Partnerships Section */}
      <section className="py-24 bg-gradient-to-br from-[#4eb5e6]/5 via-white to-[#ff8385]/5">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-gray-900">
              Have an idea? <span className="text-[#ffed00]">Let's build it together.</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
              Digital inclusion is a collaborative effort. DIL actively seeks to partner with ventures, 
              technologists, and organizations that share our vision of a more equitable digital landscape.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100 text-center group">
              <div className="w-16 h-16 bg-gradient-to-br from-[#4eb5e6]/20 to-[#4eb5e6]/10 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                <Users className="w-8 h-8 text-[#ffed00]" />
              </div>
              <h3 className="text-xl font-bold mb-3 text-gray-900">Collaborate</h3>
              <p className="text-gray-600">Partner with us to create impactful solutions</p>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100 text-center group">
              <div className="w-16 h-16 bg-gradient-to-br from-[#ff8385]/20 to-[#ff8385]/10 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                <Target className="w-8 h-8 text-[#ff8385]" />
              </div>
              <h3 className="text-xl font-bold mb-3 text-gray-900">Innovate</h3>
              <p className="text-gray-600">Build technology that serves everyone</p>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100 text-center group">
              <div className="w-16 h-16 bg-gradient-to-br from-[#4eb5e6]/20 to-[#ff8385]/20 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                <Globe className="w-8 h-8 text-[#ffed00]" />
              </div>
              <h3 className="text-xl font-bold mb-3 text-gray-900">Impact</h3>
              <p className="text-gray-600">Create lasting change for communities</p>
            </div>
          </div>

          <div className="text-center">
            <Button
              size="lg"
              onClick={() => setIsPartnershipFormOpen(true)}
              className="bg-[#ffed00] hover:bg-[#faba00] border-4 border-black text-black font-bold px-8 py-6 text-lg rounded-full shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105"
            >
              <Lightbulb className="mr-2 h-5 w-5" />
              Submit Your Idea Now
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* About Us Section */}
      <section id="about" className="py-24 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-gray-900">
              About <span className="text-[#ff8385]">Us</span>
            </h2>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-3xl shadow-xl p-8 md:p-12 border border-gray-100 mb-12">
              <p className="text-xl text-gray-700 leading-relaxed mb-6 font-semibold">
                We are a team of entrepreneurs, techies, journalists and professionals who want to solve real-world problems.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                At the core of our belief is the idea of community-building. We build communities that support each other to find tech-enabled solutions.
              </p>
            </div>

            {/* Team Members */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
              {/* Bilal Zaidi */}
              <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-100 hover:shadow-xl transition-all duration-300">
                <div className="aspect-square w-full overflow-hidden">
                  <img
                    src="https://customer-assets.emergentagent.com/job_dil-preview/artifacts/41k432ol_Screen%20Shot%202026-05-25%20at%204.50.10%20PM.png"
                    alt="Bilal Zaidi"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-8">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">Bilal Zaidi</h3>
                  <p className="text-[#ffed00] font-semibold mb-4">Founder & CEO</p>
                  <p className="text-gray-600 leading-relaxed mb-4">
                    A former TV Journalist, Bilal has been a serial entrepreneur, building award-winning high-impact startups.
                  </p>
                  <p className="text-gray-600 leading-relaxed">
                    A community builder, he maintains a bird's eye-view on the impact of new technology on everyday lives. At Digital Inclusion Lab, Bilal wishes to build solution-focussed enterprises. He has worked in New York City, Dubai and is currently based in New Delhi, India.
                  </p>
                </div>
              </div>

              {/* Mohit Bhardwaj */}
              <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-100 hover:shadow-xl transition-all duration-300">
                <div className="aspect-square w-full overflow-hidden">
                  <img
                    src="https://customer-assets.emergentagent.com/job_dil-preview/artifacts/y7ctpn70_Screen%20Shot%202026-05-25%20at%204.51.16%20PM.png"
                    alt="Mohit Bhardwaj"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-8">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">Mohit Bhardwaj</h3>
                  <p className="text-[#ffed00] font-semibold mb-4">Co-founder</p>
                  <p className="text-gray-600 leading-relaxed mb-4">
                    From Rs 20 crore tech startups to Biryani kitchens, Mohit has powered a diverse range of ventures across India.
                  </p>
                  <p className="text-gray-600 leading-relaxed">
                    He works in the intersection of technology, marketing and community building. He is currently based in Manali, India.
                  </p>
                </div>
              </div>
            </div>

            {/* Mission Points */}
            <div className="bg-[#ffed00]/20 rounded-3xl p-8 md:p-12">
              <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center">Our Mission</h3>
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#ffed00] rounded-full flex items-center justify-center flex-shrink-0 text-black font-bold text-xl">
                    1
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-gray-900 mb-2">Bridging the Digital Divide</h4>
                    <p className="text-gray-700 leading-relaxed">
                      Providing access to technology and digital literacy for vulnerable groups.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#ff8385] rounded-full flex items-center justify-center flex-shrink-0 text-black font-bold text-xl">
                    2
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-gray-900 mb-2">Fostering Inclusion & Collaboration</h4>
                    <p className="text-gray-700 leading-relaxed">
                      Creating a more inclusive and collaborative digital society. Helping outsiders get a foot in the door.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#ffed00] rounded-full flex items-center justify-center flex-shrink-0 text-black font-bold text-xl">
                    3
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-gray-900 mb-2">Unlocking Opportunities</h4>
                    <p className="text-gray-700 leading-relaxed">
                      Creating hundreds of new jobs and livelihoods by creating new ventures that solve real world problems.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Partnership Form Modal */}
      <PartnershipForm 
        open={isPartnershipFormOpen} 
        onOpenChange={setIsPartnershipFormOpen}
      />
    </div>
  );
};

export default Home;
